import { useEffect, useState } from "react";
import { AlertTriangle, DollarSign, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CostMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/project-costs");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load project costs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolveAlert = async (id) => {
    try {
      await api.post(`/admin/cost-alerts/${id}/resolve`);
      toast.success("Alert resolved");
      load();
    } catch (e) {
      toast.error("Could not resolve alert");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1F3D] flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-[#0F1F3D]" /> Cost Monitor
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Internal AI cost per project (super-admin only). Not shown to customers.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="cost-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E2E8F0] text-sm hover:bg-[#F8FAFC] min-h-[44px]"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-[#64748B]" data-testid="cost-loading">Loading…</div>
      ) : error ? (
        <div className="p-6 rounded-xl bg-[#FEF2F2] text-[#B91C1C]" data-testid="cost-error">{error}</div>
      ) : (
        <div className="space-y-5" data-testid="cost-content">
          {/* Open alerts */}
          {(data.open_alerts || []).length > 0 && (
            <div className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5" data-testid="cost-alerts">
              <h3 className="text-sm font-bold text-[#991B1B] mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Open cost alerts ({data.open_alerts.length})
              </h3>
              <div className="space-y-2">
                {data.open_alerts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-[#FECACA]">
                    <div className="text-sm text-[#0F1F3D]">
                      <span className="font-semibold">{a.project_name || a.project_id}</span>
                      <span className="text-[#64748B]"> · cost {a.total_cost} ≥ threshold {a.threshold}</span>
                    </div>
                    <button
                      onClick={() => resolveAlert(a.id)}
                      className="text-xs font-semibold text-[#0F1F3D] hover:underline"
                    >
                      Resolve
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-project cost table */}
          <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-baseline justify-between">
              <h3 className="text-sm font-bold text-[#0F1F3D]">Per-project cost</h3>
              {/* <span className="text-xs text-[#94A3B8]">Default threshold: {data.default_threshold}</span> */}
            </div>
            {(data.projects || []).length === 0 ? (
              <div className="p-8 text-center text-[#94A3B8]" data-testid="cost-empty">No AI cost recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#64748B] border-b border-[#EEF2F7]">
                      <th className="px-5 py-3 font-semibold">Project</th>
                      <th className="px-5 py-3 font-semibold">Org</th>
                      <th className="px-5 py-3 font-semibold text-right">AI calls</th>
                      <th className="px-5 py-3 font-semibold text-right">Total cost</th>
                      {/*<th className="px-5 py-3 font-semibold text-right">Threshold</th>
                      <th className="px-5 py-3 font-semibold">Status</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((p) => (
                      <tr key={p.project_id} className="border-b border-[#F1F5F9]" data-testid={`cost-row-${p.project_id}`}>
                        <td className="px-5 py-3 text-[#0F1F3D] font-medium">{p.project_name}</td>
                        <td className="px-5 py-3 text-[#64748B]">{p.org_name}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{p.ai_calls}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold">{p.total_cost}</td>
                        {/*<td className="px-5 py-3 text-right tabular-nums text-[#94A3B8]">{p.threshold}</td>
                        <td className="px-5 py-3">
                          {p.over_threshold ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] text-xs font-semibold">
                              <AlertTriangle className="h-3 w-3" /> Over
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] text-xs font-semibold">OK</span>
                          )}
                        </td>*/}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
